import React, { useCallback } from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import { bool, func } from 'prop-types';
import {
  Modal,
  Button,
  Stack,
  ButtonGroup,
  Input,
  Loader,
  TextLink,
  Text,
  Select,
} from '@chaser/components';
import usePush from '../../../../hooks/usePush';
import LabeledFieldWithError from '../../../../components/LabeledFieldWithError/index.jsx';
import { useHistory } from 'react-router';
import ConnectService from '../../../../services/connect-service';
import debounce from 'lodash/debounce';
import InfoLink from '../../../../components/InfoLink';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import { useCacheActions } from '../../../../hooks/useFetch';
import { pendo } from '../../../../util/logging';

const REGIONS = [
  { label: 'Europe (eu1)', value: 'eu_1' },
  { label: 'Europe (eu2)', value: 'eu_2' },
  { label: 'United Kingdom', value: 'uk' },
  { label: 'United States', value: 'us' },
];

const AccountsIQModal = ({ open, close, onClosed, companyId = '', region = '' }) => {
  const history = useHistory();
  const { invalidateDomain } = useCacheActions();

  const { execute, loading, error, response } = usePush(ConnectService.previewAccountsIQ);

  const { execute: eConnect, loading: cLoading } = usePush(ConnectService.connectAccountsIQ, {
    errorToast: 'Failed to connect',
  });

  const form = useFormik({
    initialValues: {
      companyID: companyId,
      userKey: '',
      region,
    },
    validate: ({ companyID, userKey, region }) => {
      const errors = {};
      if (!companyID) errors.companyID = 'Please enter a company id';
      if (!userKey) errors.userKey = 'Please enter a user key';
      if (!region) errors.region = 'Please enter a region';
      return errors;
    },
    validateOnMount: true,
    onSubmit: async ({ companyID, userKey, region }, { setSubmitting }) => {
      const [err, res] = await eConnect({
        userKey,
        organisationID: companyID,
        region,
      });
      setSubmitting(false);
      if (err) return;
      close();

      pendo.track('integration_modal_submitted', {
        integration: 'accountsiq',
        isReconnect: !!companyId,
      });

      if (!companyId) {
        history.push(`/?organisation=${res.ShortCode}`);
      } else {
        await invalidateDomain('organisation');
      }
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderPreview = useCallback(
    debounce(({ userKey, companyID, region }) => {
      if (!userKey || !companyID || !region) return;
      execute({
        userKey,
        organisationID: companyID,
        region,
      });
    }, 2000),
    [],
  );

  useOnUpdate(() => {
    renderPreview(form.values);
  }, [form.values]);

  return (
    <Modal
      title={`${companyId ? 'Reconnect' : 'Connect'} to AccountsIQ`}
      open={open}
      toggle={close}
      onClosed={onClosed}
    >
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            <Stack gap="medium">
              <Stack>
                <LabeledFieldWithError
                  disabled={!!companyId}
                  name="companyID"
                  as={Input}
                  label="Company ID"
                  width="100%"
                  placeholder="Enter ID"
                />
                <LabeledFieldWithError
                  name="userKey"
                  as={Input}
                  type="textarea"
                  height="572"
                  label={
                    <div>
                      User Key{' '}
                      <InfoLink to="https://help.chaserhq.com/" label="Info about AccountsIQ" />
                    </div>
                  }
                  width="100%"
                  placeholder="Enter user key"
                />
                <LabeledFieldWithError
                  disabled={!!region}
                  label="Region"
                  name="region"
                  as={Select}
                  width="100%"
                  placeholder="Enter Region"
                >
                  <option value="" disabled></option>
                  {REGIONS.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </LabeledFieldWithError>
                {loading && <Loader />}
                {error && (
                  <>
                    <Text color="Red">
                      Sorry. The system was not able to connect to your company.
                    </Text>
                    <Text>
                      Please verify your company information. If the problem persists, send an email
                      to{' '}
                      <TextLink as="a" monochrome href="mailto:support@chaserhq.com">
                        support@chaserhq.com
                      </TextLink>
                    </Text>
                  </>
                )}
                {response && (
                  <Stack gap="small">
                    <Text>
                      Company:{` `}
                      <Text fontWeight="Bold">{response.organisationName}</Text>
                    </Text>
                    <Text>
                      User:{` `}
                      <Text fontWeight="Bold">{response.userEmail}</Text>
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button onClick={() => close(true)} appearance="secondary" type="submit" data-trackingid="integration-accountsiq-cancel">
                Cancel
              </Button>
              <Button disabled={!form.isValid} loading={cLoading} type="submit" data-trackingid="integration-accountsiq-connect">
                Connect
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

AccountsIQModal.propTypes = {
  open: bool.isRequired,
  close: func.isRequired,
  onClosed: func,
};

export default AccountsIQModal;
